import {
    useState,
    useEffect,
    useCallback,
    type FormEvent,
} from 'react'
import type {
    PaymentAddrFieldKey,
    PaymentFormState,
    PaymentsTabProps,
    PaymentStaffOption,
    PropertyPaymentItem,
} from '../types'
import { useUserRole } from '@/hooks/useUserRole'
import { getStates, type State } from '@/lib/api/statesService'
import apiClient, { getAuthToken } from '@/lib/api/apiClient'
import { getBrowserApiBaseUrl } from '@/config/env'
import {
    showSuccess,
    showError,
    showInfo,
    formatApiError,
    showApiError,
    showValidationMessage,
} from '@/lib/utils/notifications'
import { isCompanyDuplicateAddressCancelledError } from '@/features/company/utils/companyDuplicateAddress'
import {
    getFilterValueAngularStyle,
    mergeAngularFullFormParityFields,
} from '@/features/company/utils/companyValidators'
import { loadGoogleMapsScript } from '@/lib/utils/googleMaps'
import {
    buildPaymentFormState,
    buildPropertyPaymentItem,
    getFilterValue,
    getMissingPaymentAddrFields,
    isCompanyPaymentFormDirty,
    MAX_PROPERTIES_SCROLL,
    normalizePaymentFiles,
    PAYMENT_ADDR_FIELD_ORDER,
    paymentAddrFieldTestId,
    propertyPaymentRows,
    resolveCompanyId,
    serverSaysUseCompanyPaymentForAllProperties,
} from '../utils/paymentsTabLogic'
import {
    DEFAULT_PAYMENT_LOGS_SESSION_KEY,
    MAX_PAYMENT_FILE_SIZE_MB,
} from '../constants'
import { openDefaultPaymentLogsInNewTab } from '@/lib/utils/reportsNavigation'

export function usePaymentsTab({
    company,
    companyId: companyIdFromRoute,
    onUpdate,
    onCompanyRefresh,
}: PaymentsTabProps) {
    const navigateToDefaultPaymentLogs = useCallback(
        (propertyId: string | number, propertyName: string) => {
            sessionStorage.setItem(
                DEFAULT_PAYMENT_LOGS_SESSION_KEY,
                JSON.stringify({ property_id: propertyId, property_name: propertyName }),
            )
            openDefaultPaymentLogsInNewTab()
        },
        [],
    )
    const { hasPolicy } = useUserRole()
    const companyId = resolveCompanyId(companyIdFromRoute, company)
    const [states, setStates] = useState<State[]>([])
    const [companyForm, setCompanyForm] = useState<PaymentFormState>(() => buildPaymentFormState(company))
    const [propertyForms, setPropertyForms] = useState<PropertyPaymentItem[]>([])
    const [saving, setSaving] = useState(false)
    const [uploading, setUploading] = useState<string | null>(null)
    const [downloading, setDownloading] = useState<string | null>(null)
    const [filePickerHint, setFilePickerHint] = useState<Record<string, string>>({})
    const [showUpdateAllConfirm, setShowUpdateAllConfirm] = useState(false)
    const [allPropertyPaymentTogglePending, setAllPropertyPaymentTogglePending] = useState<'0' | '1' | null>(null)
    const [switchingUsePaymentForAllProperties, setSwitchingUsePaymentForAllProperties] = useState(false)
    const [switchingPaymentForAllTarget, setSwitchingPaymentForAllTarget] = useState<'0' | '1' | null>(null)
    const [savePropertyIndex, setSavePropertyIndex] = useState<number | null>(null)
    const [showDeleteFileConfirm, setShowDeleteFileConfirm] = useState<{
        fileId: string
        type: string
        index?: number
        propertyIndex?: number
    } | null>(null)
    const [validateAddressPayload, setValidateAddressPayload] = useState<{
        propertyIndex?: number
        verification: string
        successMessage?: string
    } | null>(null)
    const [paymentAddrValidateErrors, setPaymentAddrValidateErrors] = useState<
        | { scope: 'company'; fields: Partial<Record<PaymentAddrFieldKey, true>> }
        | { scope: 'property'; propertyIndex: number; fields: Partial<Record<PaymentAddrFieldKey, true>> }
        | null
    >(null)
    const [propertyJumpToValue, setPropertyJumpToValue] = useState('')

    const canEdit = hasPolicy(['company_payment_edit'])
    const canAchFormStatus = hasPolicy(['company_ach_form_status'])
    const canDefaultPaymentLogs = hasPolicy(['reports_default_payment_logs'])
    const propertyPaymentsLocked = serverSaysUseCompanyPaymentForAllProperties(company)
    const showPropertyPaymentSection = propertyForms.length > 0

    const cancelUpdateAllPropertyConfirm = () => {
        setShowUpdateAllConfirm(false)
        setAllPropertyPaymentTogglePending(null)
    }

    const usePaymentForAllToggleDisplay =
        switchingUsePaymentForAllProperties && switchingPaymentForAllTarget !== null
            ? switchingPaymentForAllTarget
            : showUpdateAllConfirm && allPropertyPaymentTogglePending !== null
              ? allPropertyPaymentTogglePending
              : companyForm.payment_method_to_property === '1'
                ? '1'
                : companyForm.payment_method_to_property === '0'
                  ? '0'
                  : ''

    const usePaymentForAllToggleBusy =
        switchingUsePaymentForAllProperties || (showUpdateAllConfirm && allPropertyPaymentTogglePending !== null)

    const companyPaymentInfoFieldsDisabled = !canEdit || usePaymentForAllToggleBusy

    useEffect(() => {
        getStates().then(setStates).catch(console.error)
    }, [])

    /* eslint-disable react-hooks/exhaustive-deps -- sync local payment state when listed GET /api/company payment fields change; omitting full `company` avoids resets on unrelated refetches */
    useEffect(() => {
        setCompanyForm(buildPaymentFormState(company))
    }, [
        company?.['company_id'],
        company?.['payment_method'],
        company?.['verify'],
        company?.['payment_method_to_property'],
        company?.['payment_for_property'],
        company?.['routing_number'],
        company?.['routingNumber'],
        company?.['account_number'],
        company?.['accountNumber'],
        company?.['property_method'],
        company?.['company_status'],
        company?.['payee_name'],
        company?.['payment_address'],
        company?.['payment_shop'],
        company?.['payment_city'],
        company?.['payment_state'],
        company?.['payment_zipcode'],
    ])

    useEffect(() => {
        const list = propertyPaymentRows(company)
        setPropertyForms(list.map((p) => buildPropertyPaymentItem(p, company, companyForm)))
    }, [
        company?.['property_payment'],
        company?.['company_id'],
        company?.['verify'],
        company?.['payment_for_property'],
        company?.['payment_method'],
        company?.['routing_number'],
        company?.['routingNumber'],
        company?.['account_number'],
        company?.['accountNumber'],
        company?.['property_method'],
        company?.['company_status'],
        company?.['payee_name'],
        company?.['payment_address'],
        company?.['payment_shop'],
        company?.['payment_city'],
        company?.['payment_state'],
        company?.['payment_zipcode'],
        company?.['other_payee_name'],
        company?.['company_address'],
        company?.['company_city'],
        company?.['company_state'],
        company?.['company_zipcode'],
        company?.['payment_method_to_property'],
        company?.['payment_for_property'],
    ])
    /* eslint-enable react-hooks/exhaustive-deps */

    const updateCompanyForm = (updates: Partial<PaymentFormState>) => {
        setCompanyForm((prev) => ({ ...prev, ...updates }))
    }

    const updatePropertyForm = (index: number, updates: Partial<PropertyPaymentItem>) => {
        setPropertyForms((prev) => {
            const next = [...prev]
            if (!next[index]) return next
            next[index] = { ...next[index], ...updates }
            return next
        })
    }

    const clearPaymentAddrFieldError = useCallback(
        (field: PaymentAddrFieldKey, scope: 'company' | 'property', propertyIndex?: number) => {
            setPaymentAddrValidateErrors((prev) => {
                if (!prev) return null
                if (scope === 'company') {
                    if (prev.scope !== 'company') return prev
                    if (!prev.fields[field]) return prev
                    const nextFields = { ...prev.fields }
                    delete nextFields[field]
                    return Object.keys(nextFields).length === 0
                        ? null
                        : { scope: 'company', fields: nextFields }
                }
                if (
                    propertyIndex !== undefined &&
                    prev.scope === 'property' &&
                    prev.propertyIndex === propertyIndex
                ) {
                    if (!prev.fields[field]) return prev
                    const nextFields = { ...prev.fields }
                    delete nextFields[field]
                    return Object.keys(nextFields).length === 0
                        ? null
                        : { scope: 'property', propertyIndex, fields: nextFields }
                }
                return prev
            })
        },
        [],
    )

    useEffect(() => {
        setPropertyForms((prev) =>
            prev.map((row) =>
                row.pay_to === 'COMPANY' ? { ...row, payment_method: companyForm.payment_method } : row,
            ),
        )
    }, [companyForm.payment_method])

    const handleUpdateAllProperty = async () => {
        const rawTarget = allPropertyPaymentTogglePending ?? companyForm.payment_method_to_property
        const target: '0' | '1' = rawTarget === '1' ? '1' : '0'
        if (!companyId) {
            showError('Unable to save: company id is missing.')
            cancelUpdateAllPropertyConfirm()
            return
        }
        setShowUpdateAllConfirm(false)
        setAllPropertyPaymentTogglePending(null)
        setSwitchingPaymentForAllTarget(target)
        setSwitchingUsePaymentForAllProperties(true)
        setSaving(true)
        try {
            /** Angular `updateAllProperty()`: POST company only when `companyEditForm.dirty`. */
            const companyPaymentDirty = isCompanyPaymentFormDirty(companyForm, company, target)
            if (companyPaymentDirty) {
                const payeeName =
                    companyForm.payee_name === 'OTHER'
                        ? String(companyForm.other_payee_name ?? '').trim()
                        : companyForm.payee_name
                const paymentFields: Record<string, unknown> = {
                    payment_method: companyForm.payment_method,
                    verify: companyForm.verify,
                    payment_method_to_property: target,
                    routing_number: companyForm.routing_number,
                    account_number: companyForm.account_number,
                    property_method: companyForm.property_method,
                    company_status: companyForm.company_status,
                    payee_name: payeeName,
                    payment_address: companyForm.payment_address,
                    payment_shop: companyForm.payment_shop,
                    payment_city: companyForm.payment_city,
                    payment_state: companyForm.payment_state,
                    payment_zipcode: companyForm.payment_zipcode,
                }
                const filtered = getFilterValueAngularStyle(paymentFields)
                const payload = {
                    ...mergeAngularFullFormParityFields(company as Record<string, unknown> | undefined),
                    ...filtered,
                }
                await onUpdate(payload)
            }
            await apiClient.post(`/api/update_all_property/${companyId}`, {
                update_all_property: target,
            })
            showSuccess('Payment Info Save Successfully!')
            await Promise.resolve(onCompanyRefresh())
        } catch (e: unknown) {
            if (!isCompanyDuplicateAddressCancelledError(e)) {
                showApiError(e)
            }
        } finally {
            setSaving(false)
            setSwitchingUsePaymentForAllProperties(false)
            setSwitchingPaymentForAllTarget(null)
        }
    }

    const handleSaveProperty = async (index: number) => {
        setSavePropertyIndex(null)
        const row = propertyForms[index]
        if (!row || !row.property_id) return
        setSaving(true)
        try {
            const payload = getFilterValue(row as unknown as Record<string, unknown>) as Record<string, unknown>
            payload.fk_company_id = companyId
            if (payload['payee_name'] === 'OTHER') {
                payload['payee_name'] = payload['other_payee_name']
            }
            delete payload['other_payee_name']
            delete payload['ach_file']
            delete payload['cancelled_check']
            delete payload['property_name']
            await apiClient.post(`/api/property/${row.property_id}`, payload)
            showSuccess('Property Info Save Successfully!')
            onCompanyRefresh()
        } catch (e: unknown) {
            showApiError(e)
        } finally {
            setSaving(false)
        }
    }

    const handleDownloadFile = useCallback(
        async (fileId: string, fileType: string, _module: string = 'COMPANY', refId: string = companyId) => {
            setDownloading(fileId)
            const url = `${getBrowserApiBaseUrl()}/serve-file/${_module}/${refId}/${fileType}/${fileId}`
            const token = getAuthToken()
            try {
                const xhr = new XMLHttpRequest()
                xhr.open('GET', url, true)
                xhr.responseType = 'blob'
                if (token) {
                    xhr.setRequestHeader('token', token)
                }
                xhr.onload = () => {
                    setDownloading(null)
                    if (xhr.status === 200) {
                        const blob = xhr.response as Blob
                        const objectUrl = window.URL.createObjectURL(blob)
                        window.open(objectUrl, '_blank')
                    } else {
                        const blob = xhr.response as Blob
                        const showPreviewFailed = (detail: string) => {
                            const body = formatApiError({
                                response: { status: xhr.status, data: detail },
                            } as Record<string, unknown>)
                            showValidationMessage(
                                `<p style="margin:0 0 10px;font-weight:600;color:#1a1a1a;">Unable to open file</p>${body}`,
                                'Message',
                            )
                        }
                        if (blob && typeof blob.text === 'function') {
                            blob
                                .text()
                                .then((t) => {
                                    const trimmed = (t ?? '').trim()
                                    showPreviewFailed(trimmed || `Request failed (${xhr.status})`)
                                })
                                .catch(() => showPreviewFailed(`Request failed (${xhr.status})`))
                        } else {
                            showPreviewFailed(`Request failed (${xhr.status})`)
                        }
                    }
                }
                xhr.onerror = () => {
                    setDownloading(null)
                    showValidationMessage(
                        '<p style="margin:0;font-weight:600;color:#1a1a1a;">Unable to open file</p><p style="margin:10px 0 0;">Network error while loading the file.</p>',
                        'Message',
                    )
                }
                xhr.send()
            } catch {
                setDownloading(null)
                showError('Download failed')
            }
        },
        [companyId],
    )

    const handleDownloadAchForm = useCallback(async () => {
        const encodedPath = encodeURIComponent('/static/Liberty_ACH_Form.pdf')
        try {
            const { data } = await apiClient.get<Blob>(`/serve-file-config/?url=${encodedPath}`, {
                responseType: 'blob',
            })
            const objectUrl = window.URL.createObjectURL(data)
            window.open(objectUrl, '_blank')
        } catch (e: unknown) {
            showApiError(e)
        }
    }, [])

    const handleDeleteFile = async (fileId: string, type: string) => {
        setShowDeleteFileConfirm(null)
        setSaving(true)
        try {
            const url = type === 'policy_file' ? `/api/delete_policy_file/${fileId}` : `/api/delete_ach_form/${fileId}`
            await apiClient.delete(url)
            showSuccess('Document file Removed')
            onCompanyRefresh()
        } catch (e: unknown) {
            showApiError(e)
        } finally {
            setSaving(false)
        }
    }

    const handleUploadFile = async (
        type: 'ach_file' | 'cancelled_check',
        files: FileList | null,
        propertyIndex?: number,
    ) => {
        if (!files?.length) return
        const file = files[0]
        const key = propertyIndex !== undefined ? `prop_${propertyIndex}_${type}` : `company_${type}`
        if (file.size / 1024 / 1024 > MAX_PAYMENT_FILE_SIZE_MB) {
            showError('File Size Exceeds Allowable Limit of 2 MB.')
            setFilePickerHint((prev) => {
                const next = { ...prev }
                delete next[key]
                return next
            })
            return
        }
        setUploading(key)
        const formData = new FormData()
        formData.append(type + '[0]', file)
        formData.append('type', propertyIndex !== undefined ? 'PROPERTY' : 'COMPANY')
        const refId =
            propertyIndex !== undefined && propertyForms[propertyIndex]
                ? propertyForms[propertyIndex].property_id
                : companyId
        const uploadId = refId != null ? String(refId).trim() : ''
        if (!uploadId) {
            showError('Unable to upload: company or property id is missing.')
            setFilePickerHint((prev) => {
                const next = { ...prev }
                delete next[key]
                return next
            })
            setUploading(null)
            return
        }
        const uploadUrl = `/api/upload_ach_form/${uploadId}`
        try {
            await apiClient.post(uploadUrl, formData)
            showSuccess('File Uploaded Successfully!')
            setFilePickerHint((prev) => {
                const next = { ...prev }
                delete next[key]
                return next
            })
            onCompanyRefresh()
        } catch (e: unknown) {
            showApiError(e)
            setFilePickerHint((prev) => {
                const next = { ...prev }
                delete next[key]
                return next
            })
        } finally {
            setUploading(null)
        }
    }

    const handleValidatePaymentAddress = useCallback(
        async (propertyIndex?: number) => {
            const form = propertyIndex !== undefined ? propertyForms[propertyIndex] : companyForm
            if (!form) return
            const missing = getMissingPaymentAddrFields(form)
            if (Object.keys(missing).length > 0) {
                setPaymentAddrValidateErrors(
                    propertyIndex !== undefined
                        ? { scope: 'property', propertyIndex, fields: missing }
                        : { scope: 'company', fields: missing },
                )
                const firstMissing = PAYMENT_ADDR_FIELD_ORDER.find((k) => missing[k])
                if (firstMissing) {
                    const propId =
                        propertyIndex !== undefined ? propertyForms[propertyIndex]?.property_id : undefined
                    const testId = paymentAddrFieldTestId(
                        firstMissing,
                        propertyIndex !== undefined ? 'property' : 'company',
                        propId,
                    )
                    requestAnimationFrame(() => {
                        document
                            .querySelector(`[data-testid="${testId}"]`)
                            ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    })
                }
                return
            }
            setPaymentAddrValidateErrors(null)
            const verifiedYesBeforeValidate = String(form.verify ?? '').trim() === '1'
            const address = `${form.payment_address} ${form.payment_city} ${form.payment_state} ${form.payment_zipcode}`
            try {
                await loadGoogleMapsScript()
                if (!window.google?.maps?.Geocoder) {
                    showError('Maps not available')
                    return
                }
                const geocoder = new window.google.maps.Geocoder()
                geocoder.geocode(
                    { address },
                    (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
                        if (
                            status !== 'OK' ||
                            !results?.length ||
                            !results[0].geometry ||
                            results[0].partial_match
                        ) {
                            setValidateAddressPayload({ propertyIndex, verification: '0' })
                            return
                        }
                        const comp = results[0].address_components || []
                        let zipValid = false
                        let stateValid = false
                        for (const c of comp) {
                            if (c.types?.includes('postal_code') && c.long_name === form.payment_zipcode)
                                zipValid = true
                            if (
                                c.types?.includes('administrative_area_level_1') &&
                                c.short_name === form.payment_state
                            )
                                stateValid = true
                        }
                        if (!zipValid || !stateValid) {
                            setValidateAddressPayload({ propertyIndex, verification: '0' })
                            return
                        }
                        const lat = results[0].geometry.location.lat()
                        const lng = results[0].geometry.location.lng()
                        if (verifiedYesBeforeValidate) {
                            setValidateAddressPayload({
                                propertyIndex,
                                verification: '1',
                                successMessage: `Address verified with Latitude: ${lat} Longitude: ${lng}`,
                            })
                        } else if (propertyIndex !== undefined) {
                            updatePropertyForm(propertyIndex, { verify: '1' })
                            setSavePropertyIndex(propertyIndex)
                        } else {
                            updateCompanyForm({ verify: '1' })
                            showInfo('Address validated. Click Save in the header to save your changes.')
                        }
                    },
                )
            } catch {
                showError('Address validation failed')
            }
        },
        [companyForm, propertyForms],
    )

    const applyValidationResult = (verification: string, propertyIndex?: number) => {
        if (propertyIndex !== undefined) {
            updatePropertyForm(propertyIndex, { verify: verification })
            setSavePropertyIndex(propertyIndex)
        } else {
            updateCompanyForm({ verify: verification })
            showInfo('Click Save in the header to save your changes.')
        }
        setValidateAddressPayload(null)
    }

    const handleCompanyPaymentFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!canEdit) {
            showError('You do not have permission to edit payment settings.')
            return
        }
        if (!companyId) {
            showError('Unable to save: company id is missing.')
            return
        }

        const pm = companyForm.payment_method
        if (pm === 'ACH') {
            if (
                !String(companyForm.account_number ?? '').trim() ||
                !String(companyForm.routing_number ?? '').trim() ||
                !String(companyForm.property_method ?? '').trim()
            ) {
                showError('Please Enter All Required Company Payment Info')
                return
            }
            if (normalizePaymentFiles(company?.['ach_file']).length === 0) {
                showError('Please Upload ACH Form of Company Payment Info')
                return
            }
            if (normalizePaymentFiles(company?.['cancelled_check']).length === 0) {
                showError('Please Upload Cancelled Check File of Company Payment Info')
                return
            }
        } else if (pm === 'PAPER_CHECK') {
            const missingAddr = getMissingPaymentAddrFields(companyForm)
            const payeeMissing = !String(companyForm.payee_name ?? '').trim()
            if (Object.keys(missingAddr).length > 0) {
                setPaymentAddrValidateErrors({ scope: 'company', fields: missingAddr })
                const firstMissing = PAYMENT_ADDR_FIELD_ORDER.find((k) => missingAddr[k])
                if (firstMissing) {
                    const testId = paymentAddrFieldTestId(firstMissing, 'company', undefined)
                    requestAnimationFrame(() => {
                        document
                            .querySelector(`[data-testid="${testId}"]`)
                            ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    })
                }
            }
            if (payeeMissing) {
                showError('Please Enter All Required Company Payment Info')
            }
            if (Object.keys(missingAddr).length > 0 || payeeMissing) {
                return
            }
        }

        const payeeName =
            companyForm.payee_name === 'OTHER'
                ? String(companyForm.other_payee_name ?? '').trim()
                : companyForm.payee_name

        const paymentFields: Record<string, unknown> = {
            payment_method: companyForm.payment_method,
            verify: companyForm.verify,
            payment_method_to_property: companyForm.payment_method_to_property,
            routing_number: companyForm.routing_number,
            account_number: companyForm.account_number,
            property_method: companyForm.property_method,
            company_status: companyForm.company_status,
            payee_name: payeeName,
            payment_address: companyForm.payment_address,
            payment_shop: companyForm.payment_shop,
            payment_city: companyForm.payment_city,
            payment_state: companyForm.payment_state,
            payment_zipcode: companyForm.payment_zipcode,
        }
        const filtered = getFilterValueAngularStyle(paymentFields)
        const payload = {
            ...mergeAngularFullFormParityFields(company as Record<string, unknown> | undefined),
            ...filtered,
        }

        setSaving(true)
        try {
            await onUpdate(payload)
            showSuccess('Company Updated  Successfully!')
        } catch (err: unknown) {
            if (isCompanyDuplicateAddressCancelledError(err)) return
            showError(formatApiError(err))
        } finally {
            setSaving(false)
        }
    }

    const companyStaff: PaymentStaffOption[] =
        (company?.['company_staff'] as PaymentStaffOption[] | undefined) ?? []

    const displayedPropertyForms = propertyForms.slice(0, MAX_PROPERTIES_SCROLL)
    const propertyFormsScrollable = displayedPropertyForms.length > 2

    const requestAllPropertiesToggle = (target: '0' | '1') => {
        if (target === '1' && companyForm.payment_method_to_property === '1') return
        if (target === '0' && companyForm.payment_method_to_property === '0') return
        setAllPropertyPaymentTogglePending(target)
        setShowUpdateAllConfirm(true)
    }

    return {
        company,
        companyId,
        states,
        companyForm,
        propertyForms,
        saving,
        uploading,
        downloading,
        filePickerHint,
        setFilePickerHint,
        showUpdateAllConfirm,
        setShowUpdateAllConfirm,
        allPropertyPaymentTogglePending,
        setAllPropertyPaymentTogglePending,
        savePropertyIndex,
        setSavePropertyIndex,
        showDeleteFileConfirm,
        setShowDeleteFileConfirm,
        validateAddressPayload,
        setValidateAddressPayload,
        paymentAddrValidateErrors,
        canEdit,
        canAchFormStatus,
        canDefaultPaymentLogs,
        propertyPaymentsLocked,
        showPropertyPaymentSection,
        cancelUpdateAllPropertyConfirm,
        usePaymentForAllToggleDisplay,
        usePaymentForAllToggleBusy,
        switchingUsePaymentForAllProperties,
        switchingPaymentForAllTarget,
        companyPaymentInfoFieldsDisabled,
        updateCompanyForm,
        updatePropertyForm,
        clearPaymentAddrFieldError,
        handleUpdateAllProperty,
        handleSaveProperty,
        handleDownloadFile,
        handleDownloadAchForm,
        handleDeleteFile,
        handleUploadFile,
        handleValidatePaymentAddress,
        applyValidationResult,
        handleCompanyPaymentFormSubmit,
        companyStaff,
        propertyJumpToValue,
        setPropertyJumpToValue,
        displayedPropertyForms,
        propertyFormsScrollable,
        navigateToDefaultPaymentLogs,
        requestAllPropertiesToggle,
    }
}
